import { isSupabaseConfigured, getSupabaseClient } from '@/lib/supabase';
import type { Tables, TablesInsert } from '@/types/supabase';

export type Workflow = Tables<'workflows'>;

// Database service with Supabase-backed methods and safe fallbacks
export class DatabaseService {
  static async createActivityLog(data: TablesInsert<'activity_logs'>): Promise<Tables<'activity_logs'>> {
    try {
      if (!isSupabaseConfigured()) {
        return { 
          id: 0, 
          created_at: new Date().toISOString(),
          details: data.details || null,
          event_type: data.event_type || null,
          status: data.status || null,
          user_id: data.user_id || null
        };
      }
      const supabase = getSupabaseClient();
      const { data: row, error } = await supabase
        .from('activity_logs')
        .insert(data)
        .select()
        .single();
      if (error) throw error;
      return row;
    } catch (e) {
      console.warn('Activity log fallback:', e);
      return { 
        id: 0, 
        created_at: new Date().toISOString(),
        details: data.details || null,
        event_type: data.event_type || null,
        status: data.status || null,
        user_id: data.user_id || null
      };
    }
  }

  // Returns monthly usage for free-plan enforcement and display
  static async getWorkflowUsageStats(userId: string) {
    try {
      if (!isSupabaseConfigured()) {
        return {
          workflows_used: 0,
          total_workflows: 0,
          last_conversion: null,
        };
      }
      const supabase = getSupabaseClient();
      const startOfMonth = new Date();
      startOfMonth.setUTCDate(1);
      startOfMonth.setUTCHours(0, 0, 0, 0);

      // Monthly used conversions
      const { count: monthlyCount, error: countErr } = await supabase
        .from('conversions')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .gte('created_at', startOfMonth.toISOString());
      if (countErr) throw countErr;

      // Latest conversion timestamp
      const { data: latestRows, error: latestErr } = await supabase
        .from('conversions')
        .select('created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1);
      if (latestErr) throw latestErr;

      // Total conversions (all time)
      const { count: totalCount, error: totalErr } = await supabase
        .from('conversions')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId);
      if (totalErr) throw totalErr;

      return {
        workflows_used: monthlyCount || 0,
        total_workflows: totalCount || 0,
        last_conversion: latestRows?.[0]?.created_at ?? null,
      };
    } catch (e) {
      console.warn('getWorkflowUsageStats fallback:', e);
      return {
        workflows_used: 0,
        total_workflows: 0,
        last_conversion: null,
      };
    }
  }

  static async recordConversion(userId: string, data: { workflow_id?: string; node_count?: number; duration_ms?: number }): Promise<Tables<'conversions'>> {
    try {
      if (!isSupabaseConfigured()) {
        return { 
          id: 0, 
          user_id: userId, 
          workflow_id: data.workflow_id || null,
          node_count: data.node_count || null,
          duration_ms: data.duration_ms || null,
          created_at: new Date().toISOString() 
        };
      }
      const supabase = getSupabaseClient();
      const payload: TablesInsert<'conversions'> = {
        user_id: userId,
        workflow_id: data.workflow_id ?? null,
        node_count: data.node_count ?? null,
        duration_ms: data.duration_ms ?? null,
      };
      const { data: row, error } = await supabase
        .from('conversions')
        .insert(payload)
        .select()
        .single();
      if (error) throw error;
      return row;
    } catch (e) {
      console.warn('recordConversion fallback:', e);
      return { 
        id: 0, 
        user_id: userId, 
        workflow_id: data.workflow_id || null,
        node_count: data.node_count || null,
        duration_ms: data.duration_ms || null,
        created_at: new Date().toISOString() 
      };
    }
  }

  static async updateUserProfile(userId: string, updates: Partial<Tables<'users'>>): Promise<Tables<'users'>> {
    try {
      if (!isSupabaseConfigured()) {
        // Fallback for profile update - mock the result
        return {
          id: userId,
          email: '',
          full_name: updates.full_name || null,
          avatar_url: updates.avatar_url || null,
          updated_at: new Date().toISOString(),
          created_at: new Date().toISOString(),
          billing_plan: updates.billing_plan || 'free',
          credits: updates.credits ?? 0,
          stripe_customer_id: updates.stripe_customer_id || null,
          stripe_subscription_id: updates.stripe_subscription_id || null
        };
      }
      const supabase = getSupabaseClient();
      const { data: row, error } = await supabase
        .from('users')
        .upsert({ id: userId, ...updates })
        .select()
        .single();
      if (error) throw error;
      return row;
    } catch (e) {
      console.warn('updateUserProfile fallback:', e);
      return {
        id: userId,
        email: '',
        full_name: updates.full_name || null,
        avatar_url: updates.avatar_url || null,
        updated_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
        billing_plan: updates.billing_plan || 'free',
        credits: updates.credits ?? 0,
        stripe_customer_id: updates.stripe_customer_id || null,
        stripe_subscription_id: updates.stripe_subscription_id || null
      };
    }
  }

  static async createWorkflow(data: TablesInsert<'workflows'>): Promise<Workflow> {
    try {
      if (!isSupabaseConfigured()) {
        return {
          id: 'local-workflow-' + Date.now(),
          user_id: data.user_id || 'local-user',
          name: data.name || 'Untitled',
          description: data.description || null,
          n8n_json: data.n8n_json || {},
          python_script: data.python_script || '',
          status: data.status || 'draft',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          node_count: data.node_count || 0,
          version: data.version || '1.0.0',
          is_public: data.is_public || false
        } as Workflow;
      }

      const supabase = getSupabaseClient();

      // Ensure user_id is present
      if (!data.user_id) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          data.user_id = user.id;
        } else {
          throw new Error('User not authenticated');
        }
      }

      const { data: row, error } = await supabase
        .from('workflows')
        .insert(data)
        .select()
        .single();

      if (error) throw error;
      return row as Workflow;
    } catch (e) {
      console.error('Failed to create workflow:', e);
      throw e;
    }
  }

  static async recordConversionFailure(data: TablesInsert<'conversion_failures'>) {
    try {
      if (!isSupabaseConfigured()) {
        console.error('Conversion failure (local log):', data);
        return;
      }
      const supabase = getSupabaseClient();
      await supabase.from('conversion_failures').insert(data);
    } catch (e) {
      console.warn('Failed to record conversion failure:', e);
    }
  }

  static async submitFeedback(data: TablesInsert<'feedback'>) {
    try {
      if (!isSupabaseConfigured()) {
        return;
      }
      const supabase = getSupabaseClient();
      await supabase.from('feedback').insert(data);
    } catch (e) {
      console.warn('Failed to submit feedback:', e);
    }
  }

  static async getUserWorkflows(userId: string): Promise<Workflow[]> {
    try {
      if (!isSupabaseConfigured()) {
        return [];
      }
      const supabase = getSupabaseClient();
      const { data, error } = await supabase
        .from('workflows')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      return data || [];
    } catch (e) {
      console.warn('getUserWorkflows error:', e);
      return [];
    }
  }

  static async deleteWorkflow(workflowId: string): Promise<void> {
    try {
      if (!isSupabaseConfigured()) {
        return;
      }
      const supabase = getSupabaseClient();
      const { error } = await supabase
        .from('workflows')
        .delete()
        .eq('id', workflowId);
        
      if (error) throw error;
    } catch (e) {
      console.error('Failed to delete workflow:', e);
      throw e;
    }
  }
}

